// src/pages/gene.codes/bloods/BloodTestHistoryModal.tsx
import React, { useState, useMemo, useEffect } from "react";
import { Input, Select, Tag, Typography } from "antd";
import { SearchOutlined, BarChartOutlined } from "@ant-design/icons";

import { DetailDrawer, SectionCard, EmptyState } from "../../../components/shared";

import BloodAnalysisModal from "./BloodAnalysisModal";

import { Test } from "../../../types/healthcare/TestTypes";
import { Patient } from "../../../types/healthcare/PatientTypes";
import { BloodTestResult } from "../../../types/lab/BloodTestTypes";
import { analyteIdentity, bloodTestFromOrder } from "../../../utils/geneCodes/bloodTestShape";
import { calculateResultSeverity } from "../../../utils/geneCodes/bloodResultUtils";
import { useBloodRangeContext } from "../../../hooks/useBloodRangeContext";
import RangeSourceTag from "../../../components/blood/RangeSourceTag";

const { Text } = Typography;

interface BloodTestHistoryModalProps {
	isOpen: boolean;
	onClose: () => void;
	allTests: Test[];
	patient?: Patient;
}

interface TestHistory {
	testName: string;
	testCode: string;
	category: string;
	instances: Array<{
		test: Test;
		result: BloodTestResult;
		date: string;
	}>;
}

const BloodTestHistoryModal: React.FC<BloodTestHistoryModalProps> = ({ isOpen, onClose, allTests, patient }) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
	const [selectedTest, setSelectedTest] = useState<Test | null>(null);
	const [isMobile, setIsMobile] = useState(false);
	const { rangeContext } = useBloodRangeContext(patient);

	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth < 768);
		};
		checkMobile();
		window.addEventListener("resize", checkMobile);
		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	const testHistory = useMemo(() => {
		const historyMap = new Map<string, TestHistory>();

		allTests.forEach((test) => {
			const bloodTest = bloodTestFromOrder(test);
			if (!bloodTest?.results) return;

			bloodTest.results.forEach((result) => {
				const key = analyteIdentity(result);

				if (!historyMap.has(key)) {
					historyMap.set(key, {
						testName: result.test_name,
						testCode: result.test_code,
						category: result.test_category || "Other",
						instances: [],
					});
				}

				const history = historyMap.get(key)!;
				history.instances.push({
					test,
					result,
					date: String(result.collected_at || result.reported_at || test.test_date || test.created_at || ""),
				});
			});
		});

		// Sort instances by date for each test
		historyMap.forEach((history) => {
			history.instances.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
		});

		return Array.from(historyMap.values()).sort((a, b) => a.testName.localeCompare(b.testName));
	}, [allTests]);

	const categories = useMemo(() => {
		const cats = new Set<string>();
		testHistory.forEach((th) => cats.add(th.category));
		return Array.from(cats).sort();
	}, [testHistory]);

	const filteredHistory = useMemo(() => {
		return testHistory.filter((th) => {
			const matchesSearch = searchTerm === "" || th.testName.toLowerCase().includes(searchTerm.toLowerCase()) || th.testCode.toLowerCase().includes(searchTerm.toLowerCase());
			const matchesCategory = categoryFilter === null || th.category === categoryFilter;
			return matchesSearch && matchesCategory;
		});
	}, [testHistory, searchTerm, categoryFilter]);

	// Derived flag (RR-1) — each instance ages against its own collection date
	const getStatusColor = (flag: string | null): string => {
		if (flag === "C") return "var(--mg-danger)";
		if (flag === "H" || flag === "L") return "var(--mg-warning)";
		return "var(--mg-success)";
	};

	const handleTestClick = (test: Test) => {
		setSelectedTest(test);
	};

	if (testHistory.length === 0) {
		return (
			<DetailDrawer
				open={isOpen}
				onClose={onClose}
				title="Blood Test History"
				subtitle={patient ? `${patient.first_name} ${patient.last_name}` : undefined}
				width="95vw"
			>
				<EmptyState title="No blood test results available" description="Blood test history will appear here once tests are processed." />
			</DetailDrawer>
		);
	}

	return (
		<>
			<DetailDrawer
				open={isOpen}
				onClose={onClose}
				title="Blood Test History"
				subtitle={patient ? `${patient.first_name} ${patient.last_name}` : undefined}
				width="95vw"
			>
				<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
					{/* Filters */}
					<div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
						<Input placeholder="Search tests..." prefix={<SearchOutlined />} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ width: isMobile ? "100%" : 300 }} allowClear />
						<Select
							placeholder="Filter by category"
							value={categoryFilter}
							onChange={setCategoryFilter}
							style={{ width: isMobile ? "100%" : 200 }}
							allowClear
							options={[{ label: "All Categories", value: null }, ...categories.map((cat) => ({ label: cat, value: cat }))]}
						/>
						<Tag icon={<BarChartOutlined />} color="blue">
							{filteredHistory.length} tests
						</Tag>
					</div>

					{/* History cards */}
					{filteredHistory.map((history) => (
						<SectionCard key={`${history.testName}_${history.testCode}`}>
							<div style={{ marginBottom: 12 }}>
								<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
									<div>
										<Text style={{ fontSize: 14, fontWeight: 600, display: "block" }}>{history.testName}</Text>
										<Text type="secondary" style={{ fontSize: 11 }}>
											{history.testCode} · {history.category}
										</Text>
									</div>
									<Tag color="blue">{history.instances.length} results</Tag>
								</div>
							</div>

							<div
								style={{
									display: "grid",
									gridTemplateColumns: isMobile ? "repeat(auto-fill, minmax(140px, 1fr))" : "repeat(auto-fill, minmax(180px, 1fr))",
									gap: 16,
									width: "100%",
								}}
							>
								{history.instances.map((instance, idx) => {
									const severity = calculateResultSeverity(instance.result, rangeContext);
									return (
										<div
											key={idx}
											style={{
												display: "flex",
												flexDirection: "column",
												alignItems: "center",
												padding: 8,
												border: "1px solid var(--mg-border)",
												borderRadius: 8,
												cursor: "pointer",
												transition: "all 0.2s",
											}}
											onClick={() => handleTestClick(instance.test)}
											onMouseEnter={(e) => {
												e.currentTarget.style.borderColor = "var(--mg-primary)";
												e.currentTarget.style.boxShadow = "0 2px 8px var(--mg-primary-bg)";
											}}
											onMouseLeave={(e) => {
												e.currentTarget.style.borderColor = "var(--mg-border)";
												e.currentTarget.style.boxShadow = "none";
											}}
										>
											<div
												style={{
													width: "100%",
													height: isMobile ? 120 : 140,
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													position: "relative",
												}}
											>
												<div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
													<div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
														<div
															style={{
																width: isMobile ? 100 : 120,
																height: isMobile ? 100 : 120,
																borderRadius: "50%",
																background: getStatusColor(severity.flag),
																opacity: 0.15,
																position: "absolute",
															}}
														/>
														<div style={{ position: "relative", zIndex: 1 }}>
															<Text style={{ fontSize: isMobile ? 18 : 24, fontWeight: 600, display: "block", textAlign: "center", color: getStatusColor(severity.flag) }}>
																{instance.result.result_value}
															</Text>
															{instance.result.units && (
																<Text type="secondary" style={{ fontSize: isMobile ? 10 : 11, textAlign: "center", display: "block" }}>
																	{instance.result.units}
																</Text>
															)}
														</div>
													</div>
												</div>
											</div>
											<div style={{ marginTop: 8, textAlign: "center", width: "100%" }}>
												<Text style={{ fontSize: 11, fontWeight: 500, display: "block" }}>{new Date(instance.date).toLocaleDateString()}</Text>
												<div style={{ display: "flex", justifyContent: "center", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
													{severity.flag && (
														<Tag color={severity.flag === "C" ? "error" : "warning"} style={{ fontSize: 9, margin: 0 }}>
															{severity.flag === "C" ? "CRITICAL" : severity.flag === "H" ? "HIGH" : "LOW"}
														</Tag>
													)}
													<RangeSourceTag source={severity.source} labFlag={instance.result.lab_flag} />
												</div>
											</div>
										</div>
									);
								})}
							</div>
						</SectionCard>
					))}
				</div>
			</DetailDrawer>

			{selectedTest && <BloodAnalysisModal isOpen={!!selectedTest} onClose={() => setSelectedTest(null)} test={selectedTest} patient={patient} alltests={allTests} />}
		</>
	);
};

export default BloodTestHistoryModal;
